// noinspection DuplicatedCode

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Card from "@material-tailwind/react/Card";
import Button from "@material-tailwind/react/Button";
import Modal from "@material-tailwind/react/Modal";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import { useMutation } from "@tanstack/react-query";
import { fetchGet, fetchPost, fetchPostMultipartSeaweed } from "../../../fetchers";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { toast } from "react-toastify";

import "react-datepicker/dist/react-datepicker.css";
import TableMemoAll from "../../../components/tablecard/TableMemoAll";
import { TextEditor } from "./TextEditor";

import {
  GetKaryawanMinV2,
  getMinKaryawan,
} from "../../hrd/karyawan/karyawanSlice";
import { getMinBagian } from "../../master/bagian/bagianSlice";
import { Colors } from "../../../assets/Colors";
import { isRequired, useForm } from "../../../components/isRequired";
import draftToHtml from "draftjs-to-html";
import { convertToRaw } from "draft-js";

// Import komponen chart
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter,
  ComposedChart, ReferenceLine
} from 'recharts';

const MemoAllPage = () => {
  const today = new Date();
  let initialState = {
    Bulan: new Date(today.getFullYear(), today.getMonth(), 1), // Set ke awal bulan
    to: [],
    showall: "true",
  };

  const mutation = useMutation((param) => fetchGet("getInternalMemoAll", param));
  const mutationAdd = useMutation((param) =>
    fetchPost("InsertInternalMemo", param)
  );

  const validations = [
    ({ to }) => (to && to.length > 0) || { to: "To wajib diisi" },
    ({ Subject }) => isRequired(Subject) || { Subject: "Subject wajib diisi" },
    ({ Attachment }) =>
      isRequired(Attachment) || { Attachment: "Attachment wajib diisi" },
    ({ description }) =>
      isRequired(description) || { Description: "Description wajib diisi" },
  ];

  const {
    values,
    isValid,
    errors,
    touched,
    changeHandler,
    clearHandler,
    customHandler,
    changeArrayHandler,
  } = useForm(initialState, validations);
  const [buttonTouched, setButtonTouched] = useState(false);

  const dispatch = useDispatch();

  const [start] = useState(0);
  const [count] = useState(10);
  const [search, setSearch] = useState("");
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [currentData, setCurrentData] = useState({});

  // State untuk data chart
  const [chartData, setChartData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [durationAnalysisData, setDurationAnalysisData] = useState([]);
  const [completionTimeData, setCompletionTimeData] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalMemos: 0,
    avgDuration: 0,
    maxDuration: 0,
    minDuration: 0,
    closedMemos: 0,
    avgClosedDuration: 0
  });
  const [activeChart, setActiveChart] = useState("duration");
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [lastFilterDate, setLastFilterDate] = useState(null);

  const mutationFile = useMutation((formData) =>
    fetchPostMultipartSeaweed(formData)
  );

  // Fungsi untuk menghitung durasi dalam hari
  const calculateDuration = (startDate, endDate) => {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (error) {
      console.error("Error calculating duration:", error);
      return 0;
    }
  };

  // Fungsi untuk memproses data menjadi format chart dengan analisis durasi
  const processChartData = (data) => {
    console.log("📊 Processing chart data:", data?.data?.length || 0, "items");
    
    if (!data || !data.data || data.data.length === 0) {
      console.log("📭 No data to process, resetting charts");
      // Reset semua data jika tidak ada data
      setChartData([]);
      setStatusData([]);
      setTimeSeriesData([]);
      setDurationAnalysisData([]);
      setCompletionTimeData([]);
      setPerformanceData([]);
      setSummaryStats({
        totalMemos: 0,
        avgDuration: 0,
        maxDuration: 0,
        minDuration: 0,
        closedMemos: 0,
        avgClosedDuration: 0
      });
      return;
    }
    
    const memos = data.data;
    const fromStats = {};
    const statusStats = { open: 0, close: 0, progress: 0 };
    const timeSeries = {};
    const durationBuckets = {
      "1 hari": 0,
      "2-3 hari": 0,
      "4-7 hari": 0,
      "1-2 minggu": 0,
      ">2 minggu": 0
    };
    
    let totalDuration = 0;
    let closedDurationTotal = 0;
    let closedCount = 0;
    let maxDuration = 0;
    let minDuration = Infinity;

    memos.forEach((memo, index) => {
      const from = memo.From || 'Unknown';
      const duration = memo.Datediff || calculateDuration(memo.Date, memo.DateEnd);
      
      // Debug log untuk beberapa item pertama
      if (index < 3) {
        console.log(`📝 Memo ${index + 1}:`, {
          id: memo.ID,
          from,
          duration,
          date: memo.Date,
          dateEnd: memo.DateEnd,
          approved: memo.Approved
        });
      }
      
      // Update summary stats
      totalDuration += duration;
      if (duration > maxDuration) maxDuration = duration;
      if (duration < minDuration) minDuration = duration;
      
      // Hitung berdasarkan From
      fromStats[from] = fromStats[from] || { count: 0, totalDuration: 0 };
      fromStats[from].count += 1;
      fromStats[from].totalDuration += duration;
      
      // Hitung berdasarkan status sesuai dengan aturan baru
      if (memo.Approved === 99) {
        statusStats.close += 1;
        closedDurationTotal += duration;
        closedCount += 1;
      } else if (memo.Approved === -1) {
        statusStats.open += 1;
      } else if (memo.Approved === 0) {
        statusStats.progress += 1;
      }
      
      // Bucket duration
      if (duration === 1) {
        durationBuckets["1 hari"] += 1;
      } else if (duration >= 2 && duration <= 3) {
        durationBuckets["2-3 hari"] += 1;
      } else if (duration >= 4 && duration <= 7) {
        durationBuckets["4-7 hari"] += 1;
      } else if (duration >= 8 && duration <= 14) {
        durationBuckets["1-2 minggu"] += 1;
      } else {
        durationBuckets[">2 minggu"] += 1;
      }
      
      // Hitung berdasarkan waktu (bulan) - ambil dari Date field
      if (memo.Date) {
        try {
          const date = new Date(memo.Date);
          const monthYear = `${date.getMonth()+1}/${date.getFullYear()}`;
          timeSeries[monthYear] = timeSeries[monthYear] || { count: 0, totalDuration: 0 };
          timeSeries[monthYear].count += 1;
          timeSeries[monthYear].totalDuration += duration;
        } catch (error) {
          console.error("Error processing date:", error);
        }
      }
    });
    
    // Format data untuk chart From dengan durasi rata-rata
    const fromData = Object.keys(fromStats).map(from => ({
      name: from,
      count: fromStats[from].count,
      avgDuration: Math.round((fromStats[from].totalDuration / fromStats[from].count) * 10) / 10
    })).sort((a, b) => b.count - a.count);
    
    console.log("👥 From data:", fromData);
    
    // Format data untuk chart status
    const statusChartData = [
      { 
        name: 'Open', 
        value: statusStats.open,
        color: '#FF8042'
      },
      { 
        name: 'Close', 
        value: statusStats.close,
        color: '#00C49F'
      },
      { 
        name: 'Progress', 
        value: statusStats.progress,
        color: '#0088FE'
      }
    ];
    
    console.log("📊 Status data:", statusChartData);
    
    // Format data untuk time series dengan avg duration
    const timeSeriesChartData = Object.keys(timeSeries).map(key => ({
      name: key,
      count: timeSeries[key].count,
      avgDuration: Math.round((timeSeries[key].totalDuration / timeSeries[key].count) * 10) / 10
    })).sort((a, b) => {
      const [aMonth, aYear] = a.name.split('/').map(Number);
      const [bMonth, bYear] = b.name.split('/').map(Number);
      return aYear === bYear ? aMonth - bMonth : aYear - bYear;
    });
    
    // Format data untuk duration analysis
    const durationAnalysis = Object.keys(durationBuckets).map(key => ({
      range: key,
      count: durationBuckets[key]
    }));
    
    console.log("⏱️ Duration analysis:", durationAnalysis);
    
    // Data untuk scatter plot completion time (hanya closed memos)
    const completionTimes = memos
      .filter(memo => memo.Approved === 99)
      .map(memo => ({
        id: memo.ID,
        duration: memo.Datediff || calculateDuration(memo.Date, memo.DateEnd),
        subject: memo.Subject,
        date: new Date(memo.Date).toLocaleDateString('id-ID')
      }));
    
    console.log("🎯 Completion times:", completionTimes.length);
    
    // Hitung summary statistics
    const avgDuration = memos.length > 0 ? totalDuration / memos.length : 0;
    const avgClosedDuration = closedCount > 0 ? closedDurationTotal / closedCount : 0;
    
    const stats = {
      totalMemos: memos.length,
      avgDuration: Math.round(avgDuration * 10) / 10,
      maxDuration: maxDuration,
      minDuration: minDuration === Infinity ? 0 : minDuration,
      closedMemos: closedCount,
      avgClosedDuration: Math.round(avgClosedDuration * 10) / 10
    };
    
    console.log("📈 Summary stats:", stats);
    
    setSummaryStats(stats);
    setChartData(fromData);
    setStatusData(statusChartData);
    setTimeSeriesData(timeSeriesChartData);
    setDurationAnalysisData(durationAnalysis);
    setCompletionTimeData(completionTimes);
    
    // Data untuk performance by user
    const performanceByUser = {};
    memos.forEach(memo => {
      const user = memo.CreatedBy || 'Unknown';
      const duration = memo.Datediff || calculateDuration(memo.Date, memo.DateEnd);
      performanceByUser[user] = performanceByUser[user] || { count: 0, totalDuration: 0 };
      performanceByUser[user].count += 1;
      performanceByUser[user].totalDuration += duration;
    });
    
    const perfData = Object.keys(performanceByUser).map(user => ({
      name: user,
      count: performanceByUser[user].count,
      avgDuration: Math.round((performanceByUser[user].totalDuration / performanceByUser[user].count) * 10) / 10
    })).sort((a, b) => a.avgDuration - b.avgDuration).slice(0, 10);
    
    setPerformanceData(perfData);
  };

  // Fungsi untuk fetch data berdasarkan bulan
  const fetchDataByMonth = (monthDate) => {
    if (!monthDate) return;
    
    const bulan = (monthDate.getMonth() + 1).toString();
    const tahun = monthDate.getFullYear().toString();
    
    console.log("📅 Fetching data for:", { bulan, tahun });
    
    const formData = {
      start: start,
      count: count,
      search: search,
      Bulan: bulan,
      Tahun: tahun,
      showall: "true",
    };
    
    return mutation.mutate(formData);
  };

  // Fetch data pertama kali
  useEffect(() => {
    console.log("🚀 Initial fetch");
    fetchDataByMonth(values.Bulan);
  }, []);

  // Handle ketika mutation selesai
  useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      console.log("✅ Data fetched successfully:", mutation.data.data?.length);
      processChartData(mutation.data);
      setIsChartLoading(false);
    }
    
    if (mutation.isError) {
      console.error("❌ Error fetching data:", mutation.error);
      setIsChartLoading(false);
      toast.error("Gagal memuat data", {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  }, [mutation.isSuccess, mutation.isError, mutation.data, mutation.error]);

  const filterApi = async () => {
    console.log("🔄 Filter API called for month:", values.Bulan);
    
    // Cek jika bulan sama dengan sebelumnya
    const currentMonthKey = values.Bulan?.toISOString().slice(0, 7);
    if (lastFilterDate === currentMonthKey) {
      console.log("⚠️ Same month, skipping fetch");
      return;
    }
    
    setIsChartLoading(true);
    setLastFilterDate(currentMonthKey);
    
    // Reset semua state chart terlebih dahulu
    setChartData([]);
    setStatusData([]);
    setTimeSeriesData([]);
    setDurationAnalysisData([]);
    setCompletionTimeData([]);
    setPerformanceData([]);
    setSummaryStats({
      totalMemos: 0,
      avgDuration: 0,
      maxDuration: 0,
      minDuration: 0,
      closedMemos: 0,
      avgClosedDuration: 0
    });
    
    await fetchDataByMonth(values.Bulan);
  };

  function handleSearch(searchValue) {
    setSearch(searchValue);
    fetchDataByMonth(values.Bulan);
  }

  function ClickAdd() {
    setShowModalAdd(true);
    clearHandler();
    setButtonTouched(false);
    dispatch(getMinBagian({ search: "" }));
    dispatch(GetKaryawanMinV2({ search: "" }));
  }

  useEffect(() => {
    if (showModalEdit && !showModalAdd) {
      setShowModalAdd(showModalEdit);
      console.log(currentData);
    }
  }, [showModalEdit]);

  const Kirim = () => {
    if (values["NamaFile"]) {
      try {
        const { v4: uuidv4 } = require("uuid");
        const uuid = uuidv4();
        let formData = new FormData();
        const nama = uuid + "-" + values["NamaFile"].name.replaceAll(" ", "_");
        formData.append("file", values["NamaFile"], nama);
        mutationFile.mutate(formData);
        values["NamaFile"] = nama;
      } catch (error) {
        console.log(error);
      }
    }

    const memoData = {
      to: values.to,
      cc: values.Cc,
      subject: values.Subject,
      desc: values.description,
      file: values.NamaFile,
    };

    mutationAdd.mutate(memoData, {
      onSuccess: async (data, context) => {
        toast.success(data.message, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        setShowModalAdd(false);
        setShowModalEdit(false);
        fetchDataByMonth(values.Bulan);
      },
      onError: async (data, context) => {
        toast.error(data.message, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      },
    });
  };

  // Komponen untuk statistik ringkas
  const StatCard = ({ title, value, unit, description, color }) => (
    <div className={`p-4 rounded shadow ${color || 'bg-white'}`}>
      <h4 className="font-semibold text-sm text-gray-600">{title}</h4>
      <div className="flex items-end mt-2">
        <p className="text-2xl font-bold">{value}</p>
        {unit && <span className="ml-1 text-sm text-gray-500">{unit}</span>}
      </div>
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
    </div>
  );

  // Komponen untuk menampilkan loading skeleton
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-gray-200 p-4 rounded h-24"></div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-200 p-4 rounded h-64"></div>
        <div className="bg-gray-200 p-4 rounded h-64"></div>
      </div>
    </div>
  );

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>

        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card>
                <div className="flex flex-row flex-wrap items-start justify-start w-full px-2 py-2 gap-4">
                  <div className="w-64">
                    <HrdInputGenerator
                      type={"bulan"}
                      name={"Bulan"}
                      title={"Bulan"}
                      value={values}
                      required={true}
                      onChange={(value) => {
                        customHandler(value, "Bulan");
                      }}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={filterApi}
                      disabled={isChartLoading}
                      className="text-main-100 hover:text-white border border-main-100 hover:bg-main-100 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isChartLoading ? (
                        <>
                          <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                          Memuat...
                        </>
                      ) : (
                        "Filter Data"
                      )}
                    </button>
                  </div>
                  <div className="flex items-end ml-auto">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Cari memo..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="absolute left-3 top-2.5">
                        🔍
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dashboard Grafik dengan Fokus pada Durasi */}
                <div className="p-4">
                  <h2 className="text-xl font-bold mb-4">
                    Analisis Durasi Penyelesaian Memo - 
                    <span className="text-blue-600 ml-2">
                      {values.Bulan ? new Date(values.Bulan).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' }) : 'Semua Bulan'}
                    </span>
                    {mutation.isLoading && (
                      <span className="ml-2 text-sm text-gray-500">
                        (memuat data...)
                      </span>
                    )}
                  </h2>
                  
                  {/* Tampilkan loading state */}
                  {(isChartLoading || mutation.isLoading) && <LoadingSkeleton />}
                  
                  {/* Tampilkan pesan jika tidak ada data */}
                  {!isChartLoading && !mutation.isLoading && summaryStats.totalMemos === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <div className="text-gray-400 text-6xl mb-4">📭</div>
                      <p className="text-gray-500 text-lg">Tidak ada data memo untuk periode ini</p>
                      <p className="text-sm text-gray-400 mt-2">
                        Filter bulan: {values.Bulan ? new Date(values.Bulan).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' }) : '-'}
                      </p>
                      <button
                        onClick={() => fetchDataByMonth(new Date())}
                        className="mt-4 text-blue-500 hover:text-blue-700 underline"
                      >
                        Coba bulan ini
                      </button>
                    </div>
                  )}
                  
                  {/* Tampilkan chart hanya jika ada data */}
                  {!isChartLoading && !mutation.isLoading && summaryStats.totalMemos > 0 && (
                    <>
                      {/* Statistik Ringkas */}
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                        <StatCard 
                          title="Total Memo" 
                          value={summaryStats.totalMemos}
                          color="bg-blue-50"
                        />
                        <StatCard 
                          title="Rata-rata Durasi" 
                          value={summaryStats.avgDuration}
                          unit="hari"
                          description="Semua memo"
                          color="bg-green-50"
                        />
                        <StatCard 
                          title="Memo Closed" 
                          value={summaryStats.closedMemos}
                          description="Sudah selesai"
                          color="bg-teal-50"
                        />
                        <StatCard 
                          title="Durasi Closed" 
                          value={summaryStats.avgClosedDuration}
                          unit="hari"
                          description="Rata-rata memo closed"
                          color="bg-purple-50"
                        />
                        <StatCard 
                          title="Durasi Tercepat" 
                          value={summaryStats.minDuration}
                          unit="hari"
                          color="bg-green-100"
                        />
                        <StatCard 
                          title="Durasi Terlama" 
                          value={summaryStats.maxDuration}
                          unit="hari"
                          color="bg-red-100"
                        />
                      </div>
                      
                      {/* Filter dan Toggle Chart */}
                      <div className="flex flex-col md:flex-row justify-between mb-6 gap-4">
                        <div className="flex space-x-2">
                          <select 
                            className="border rounded p-2 text-sm"
                            value={selectedFrom || ""}
                            onChange={(e) => setSelectedFrom(e.target.value || null)}
                          >
                            <option value="">Semua Pengirim</option>
                            {chartData.map(item => (
                              <option key={item.name} value={item.name}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex space-x-2 flex-wrap">
                          <button 
                            className={`px-3 py-1 rounded text-sm ${activeChart === 'duration' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            onClick={() => setActiveChart('duration')}
                          >
                            Analisis Durasi
                          </button>
                          <button 
                            className={`px-3 py-1 rounded text-sm ${activeChart === 'performance' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            onClick={() => setActiveChart('performance')}
                          >
                            Performa User
                          </button>
                          <button 
                            className={`px-3 py-1 rounded text-sm ${activeChart === 'distribution' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            onClick={() => setActiveChart('distribution')}
                          >
                            Distribusi
                          </button>
                          <button 
                            className={`px-3 py-1 rounded text-sm ${activeChart === 'trend' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            onClick={() => setActiveChart('trend')}
                          >
                            Trend Waktu
                          </button>
                        </div>
                      </div>
                      
                      {/* Chart berdasarkan tipe aktif */}
                      {activeChart === 'duration' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {/* Distribusi Durasi */}
                          <div className="bg-white p-4 rounded shadow">
                            <h3 className="text-lg font-semibold mb-4">Distribusi Waktu Penyelesaian</h3>
                            <div className="h-64">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={durationAnalysisData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="range" angle={-45} textAnchor="end" height={60} />
                                  <YAxis />
                                  <Tooltip />
                                  <Bar dataKey="count" fill="#8884d8" name="Jumlah Memo" />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                              Distribusi waktu yang dibutuhkan untuk menyelesaikan memo
                            </p>
                          </div>
                          
                          {/* Scatter Plot Completion Time */}
                          <div className="bg-white p-4 rounded shadow">
                            <h3 className="text-lg font-semibold mb-4">Waktu Penyelesaian per Memo (Closed)</h3>
                            <div className="h-64">
                              <ResponsiveContainer width="100%" height="100%">
                                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                  <CartesianGrid />
                                  <XAxis 
                                    type="number" 
                                    dataKey="id" 
                                    name="ID Memo" 
                                    domain={['dataMin', 'dataMax']}
                                  />
                                  <YAxis 
                                    type="number" 
                                    dataKey="duration" 
                                    name="Durasi (hari)"
                                    label={{ value: 'Durasi (hari)', angle: -90, position: 'insideLeft' }}
                                  />
                                  <Tooltip 
                                    formatter={(value) => [`${value} hari`, 'Durasi']}
                                    labelFormatter={(label) => `Memo #${label}`}
                                  />
                                  <Scatter name="Memo" data={completionTimeData} fill="#8884d8" />
                                  <ReferenceLine 
                                    y={summaryStats.avgClosedDuration} 
                                    stroke="red" 
                                    strokeDasharray="3 3"
                                    label={{ 
                                      value: `Rata-rata: ${summaryStats.avgClosedDuration} hari`, 
                                      position: 'top' 
                                    }}
                                  />
                                </ScatterChart>
                              </ResponsiveContainer>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                              Setiap titik mewakili satu memo yang sudah closed
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {activeChart === 'performance' && (
                        <div className="grid grid-cols-1 gap-6 mb-6">
                          {/* Performa User berdasarkan durasi rata-rata */}
                          <div className="bg-white p-4 rounded shadow">
                            <h3 className="text-lg font-semibold mb-4">Top 10 User dengan Durasi Rata-rata Tercepat</h3>
                            <div className="h-80">
                              <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={performanceData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                                  <YAxis yAxisId="left" />
                                  <YAxis yAxisId="right" orientation="right" />
                                  <Tooltip />
                                  <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Jumlah Memo" />
                                  <Line yAxisId="right" type="monotone" dataKey="avgDuration" stroke="#ff7300" name="Durasi Rata-rata (hari)" />
                                </ComposedChart>
                              </ResponsiveContainer>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                              User dengan rata-rata durasi penyelesaian tercepat (semakin rendah semakin baik)
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {activeChart === 'distribution' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {/* Durasi per From */}
                          <div className="bg-white p-4 rounded shadow">
                            <h3 className="text-lg font-semibold mb-4">Rata-rata Durasi per Pengirim</h3>
                            <div className="h-64">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={selectedFrom ? chartData.filter(d => d.name === selectedFrom) : chartData.slice(0, 8)}
                                  margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" angle={-45} textAnchor="end" />
                                  <YAxis yAxisId="left" />
                                  <YAxis yAxisId="right" orientation="right" />
                                  <Tooltip />
                                  <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Jumlah Memo" />
                                  <Line yAxisId="right" type="monotone" dataKey="avgDuration" stroke="#ff7300" name="Durasi Rata-rata" />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                          
                          {/* Status dengan durasi rata-rata */}
                          <div className="bg-white p-4 rounded shadow">
                            <h3 className="text-lg font-semibold mb-4">Status Memo</h3>
                            <div className="h-64">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={statusData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {statusData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="mt-4 text-sm space-y-1">
                              <p><span className="inline-block w-3 h-3 bg-[#FF8042] mr-2"></span> Open: {statusData.find(d => d.name === 'Open')?.value || 0} memo</p>
                              <p><span className="inline-block w-3 h-3 bg-[#00C49F] mr-2"></span> Close: {statusData.find(d => d.name === 'Close')?.value || 0} memo</p>
                              <p><span className="inline-block w-3 h-3 bg-[#0088FE] mr-2"></span> Progress: {statusData.find(d => d.name === 'Progress')?.value || 0} memo</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {activeChart === 'trend' && (
                        <div className="grid grid-cols-1 gap-6 mb-6">
                          {/* Trend waktu penyelesaian */}
                          <div className="bg-white p-4 rounded shadow">
                            <h3 className="text-lg font-semibold mb-4">Trend Waktu Penyelesaian per Bulan</h3>
                            <div className="h-64">
                              <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={timeSeriesData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis yAxisId="left" />
                                  <YAxis yAxisId="right" orientation="right" />
                                  <Tooltip />
                                  <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Jumlah Memo" />
                                  <Line yAxisId="right" type="monotone" dataKey="avgDuration" stroke="#ff7300" name="Durasi Rata-rata (hari)" />
                                </ComposedChart>
                              </ResponsiveContainer>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                              Trend jumlah memo dan rata-rata durasi penyelesaian per bulan
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Insight dan Rekomendasi */}
                      <div className="bg-yellow-50 p-4 rounded shadow mt-6">
                        <h3 className="text-lg font-semibold mb-2">Insight dari Data</h3>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>Rata-rata waktu penyelesaian memo: <strong>{summaryStats.avgDuration} hari</strong></li>
                          <li>Memo yang sudah closed membutuhkan rata-rata <strong>{summaryStats.avgClosedDuration} hari</strong></li>
                          <li><strong>{Math.round((summaryStats.closedMemos / summaryStats.totalMemos) * 100)}%</strong> memo sudah selesai (closed)</li>
                          {summaryStats.avgDuration > 3 && (
                            <li className="text-red-600">
                              ⚠️ Durasi rata-rata di atas 3 hari menunjukkan potensi bottleneck dalam proses
                            </li>
                          )}
                          {performanceData.length > 0 && performanceData[0].avgDuration < 1 && (
                            <li className="text-green-600">
                              ✓ {performanceData[0].name} memiliki performa terbaik dengan rata-rata {performanceData[0].avgDuration} hari
                            </li>
                          )}
                        </ul>
                      </div>
                    </>
                  )}
                </div>

                {/* Tombol untuk menambah memo */}
                <div className="flex justify-between items-center p-4 border-t">
                  <div className="text-sm text-gray-500">
                    {summaryStats.totalMemos > 0 ? (
                      <>Menampilkan {summaryStats.totalMemos} memo</>
                    ) : (
                      <>Tidak ada data</>
                    )}
                  </div>
                  <div>
                    <Button
                      color="lightBlue"
                      buttonType="filled"
                      size="regular"
                      rounded={false}
                      block={false}
                      iconOnly={false}
                      ripple="light"
                      onClick={ClickAdd}
                    >
                      + Tambah Memo Baru
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Modal untuk menambah/edit memo */}
      <Modal
        size="lg"
        fullscreen={true}
        active={showModalAdd}
        toggler={() => {
          setShowModalAdd(true);
        }}
      >
        <ModalHeader
          className="mt-20"
          toggler={() => {
            setShowModalAdd(false);
            setShowModalEdit(false);
            setButtonTouched(false);
            clearHandler();
          }}
        >
          {showModalEdit ? "Edit Data" : "Tambah Data"}
        </ModalHeader>
        <ModalBody>
          <form className="w-full max-w-lg" onSubmit={(e) => e.preventDefault()}>
            <HrdInputGenerator
              type={"multipleSelect"}
              value={values}
              name={"to"}
              title={"To"}
              onChange={changeArrayHandler}
              touched={touched}
              buttonTouched={buttonTouched}
              size={"full"}
              required={true}
              errors={errors}
              disabled={false}
            />

            <HrdInputGenerator
              type={"multipleSelect"}
              value={values}
              currentData={currentData}
              name={"Cc"}
              title={"Cc"}
              onChange={changeArrayHandler}
              touched={touched}
              buttonTouched={buttonTouched}
              size={"full"}
              required={true}
              errors={errors}
              disabled={false}
            />

            <div className={"w-full px-3 mb-6"}>
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Subject *
              </label>
              <input
                value={values.Subject}
                onChange={changeHandler}
                name="Subject"
                className={
                  (touched.Subject || buttonTouched) && errors.Subject
                    ? "appearance-none block w-full text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight outline-none bg-white"
                    : "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                }
                type="text"
                disabled={showModalEdit}
              />
            </div>

            <div className={"w-full px-3 mb-6"}>
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-6">
                {values["NamaFile"] && "Ganti "}
                Attachment
              </label>
              <label
                htmlFor={"NamaFile"}
                className="block w-full border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500 cursor-pointer hover:bg-gray-50"
              >
                {values["NamaFile"] ? (
                  <span>
                    File Selected -{" "}
                    {values["NamaFile"].name
                      ? values["NamaFile"].name
                      : values["NamaFile"]}
                  </span>
                ) : (
                  <span>Choose a file...</span>
                )}
              </label>

              <input
                type="file"
                id={"NamaFile"}
                key={"NamaFile"}
                name={"NamaFile"}
                style={{ visibility: "hidden" }}
                className="block border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                onChange={(e) => {
                  customHandler(e.target.files[0], "NamaFile");
                }}
              />
            </div>
            <div className={"w-full px-3 mb-6"}>
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Description
              </label>
              <TextEditor
                onClick={changeHandler}
                onChange={(e) =>
                  customHandler(
                    draftToHtml(convertToRaw(e.getCurrentContent())),
                    "description"
                  )
                }
              />
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="red"
            buttonType="link"
            onClick={() => {
              setShowModalAdd(false);
              setShowModalEdit(false);
              clearHandler();
            }}
            ripple="dark"
          >
            Cancel
          </Button>

          <Button
            color="green"
            onClick={Kirim}
            disabled={!isValid}
          >
            Submit
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MemoAllPage;